import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import InfiniteScroll from 'react-infinite-scroll-component';

import { EventOrganizer } from 'types/event_organizers.types';
import { Button } from '@epoch-ui/components/Button';
import { SearchInput } from '@epoch-ui/components/SearchInput';
import { Skeleton } from '@epoch-ui/components/Skeleton/skeleton';
import { ComboBox } from '@epoch-ui/components/ComboBox/ComboBox';
import * as CommandPrimitives from '@epoch-ui/components/Command/command';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalDescription,
  ModalFooter
} from '@epoch-ui/components/Modal/modal';

import { eventOrganizersApi } from '../../../../api/eventOrganizers';
import { useEditableEvent } from '../../../../hooks/useEditableEvent';

interface EditEventOrganizerModalProps extends React.ComponentProps<typeof Modal> {
  onSubmit: (value?: EventOrganizer) => void | Promise<unknown>;
}

const EditEventOrganizerModal: React.FC<EditEventOrganizerModalProps> = ({
  onSubmit,
  onOpenChange,
  ...props
}) => {
  const event = useEditableEvent();
  const [selectedOrganizer, setSelectedOrganizer] = useState<EventOrganizer | undefined>(
    event.organizer
  );
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchText, setSearchText] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const organizersQuery = useInfiniteQuery({
    queryKey: ['organizers', searchText],
    queryFn: ({ pageParam = 1 }) =>
      eventOrganizersApi.list({ searchText, page: pageParam, perPage: 25 }),
    getNextPageParam: (lastPage) =>
      lastPage.page < lastPage.pages ? lastPage.page + 1 : undefined,
    initialPageParam: 1
  });

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      await onSubmit(selectedOrganizer);
    } finally {
      setIsSubmitting(false);
    }
  }, [onSubmit, selectedOrganizer]);

  const isDirty = useMemo(() => {
    if (!selectedOrganizer) {
      return false;
    }
    return selectedOrganizer.email !== event.organizer?.email;
  }, [selectedOrganizer, event.organizer]);

  const resetState = useCallback(() => {
    setSelectedOrganizer(event.organizer);
    setSearchText(null);
    setDropdownOpen(false);
  }, [event.organizer]);

  // Effect to reset state when event object changes
  useEffect(() => {
    resetState();
  }, [event.organizer]);

  const organizersList = useMemo(() => {
    return organizersQuery.data?.pages.flatMap((page) => page.items) ?? [];
  }, [organizersQuery.data]);

  return (
    <Modal
      {...props}
      onOpenChange={(open) => {
        onOpenChange?.(open);

        // Reset state when modal is closed
        if (!open) {
          resetState();
        }
      }}
    >
      <ModalContent>
        <ModalHeader>
          <ModalTitle>Organizer</ModalTitle>
        </ModalHeader>
        <ModalDescription>
          Choose an employee as the main point of contact for this event. Organizers appear on the
          event page and receive email replies.
        </ModalDescription>
        <div className="tw-my-5">
          <ComboBox
            isOpen={dropdownOpen}
            onOpenChange={setDropdownOpen}
            className="tw-text-left"
            contentAvoidCollisions
            triggerContent={
              selectedOrganizer ? (
                <span className="tw-text-wrap tw-text-foreground">
                  {selectedOrganizer?.name}{' '}
                  <span className="tw-text-muted-foreground">({selectedOrganizer?.email})</span>
                </span>
              ) : (
                'Select an organizer'
              )
            }
          >
            <CommandPrimitives.Command shouldFilter={false}>
              <SearchInput
                placeholder="Search for an organizer"
                value={searchText || ''}
                onChange={(e) => setSearchText(e.target.value || null)}
              />
              <CommandPrimitives.CommandList className="tw-overflow-hidden">
                {organizersQuery.isLoading && <Skeleton className="tw-h-7 tw-w-28" />}
                {!organizersQuery.isLoading &&
                  searchText &&
                  organizersQuery.data?.pages[0].items.length === 0 && (
                    <p className="tw-mx-auto tw-my-6 tw-text-center tw-text-muted-foreground">
                      No organizers found.
                    </p>
                  )}
                <CommandPrimitives.CommandGroup>
                  <InfiniteScroll
                    dataLength={organizersList.length}
                    next={organizersQuery.fetchNextPage}
                    hasMore={organizersQuery.hasNextPage}
                    loader={<Skeleton className="tw-h-7 tw-w-28" />}
                    height={300}
                  >
                    {organizersList.map((organizer) => (
                      <CommandPrimitives.CommandItem
                        key={organizer.email}
                        onSelect={() => {
                          setSelectedOrganizer(organizer);
                          setDropdownOpen(false);
                        }}
                      >
                        {organizer.name} ({organizer.email})
                      </CommandPrimitives.CommandItem>
                    ))}
                  </InfiniteScroll>
                </CommandPrimitives.CommandGroup>
              </CommandPrimitives.CommandList>
            </CommandPrimitives.Command>
          </ComboBox>
        </div>
        <ModalFooter className="tw-mt-0">
          <Button
            className="tw-w-full"
            onClick={handleSubmit}
            loading={isSubmitting}
            disabled={isSubmitting || !isDirty}
          >
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export { EditEventOrganizerModal };
